import type { Except, Optional } from '@festina-life/core'
import { useCallback, useMemo } from 'react'
import {
  NumericFormat,
  type NumericFormatProps,
  numericFormatter,
  type OnValueChange,
} from 'react-number-format'

import { RawTextInput, type RawTextInputProps } from '../textInput/RawTextInput'
import { createNumberInputKeyDownHandler } from './createNumberInputKeyDownHandler'
import { useNumberFormatOptions } from './useNumberFormatOptions'

export type RawNumberInputProps = Except<
  RawTextInputProps,
  'value' | 'onChange' | 'type' | 'defaultValue' | 'placeholder'
> & {
  value?: Optional<number>
  name?: string
  defaultValue?: Optional<string | number>
  placeholder?: Optional<string | number>
  onChange?: (value: number | undefined) => void
} & Pick<NumericFormatProps, 'decimalScale' | 'thousandSeparator'>

export const RawNumberInput = ({
  value,
  onChange,
  name,
  placeholder,
  decimalScale,
  onKeyDown,
  ...restProps
}: RawNumberInputProps) => {
  const numberFormatOptions = useNumberFormatOptions(decimalScale)
  const handleKeyDown = createNumberInputKeyDownHandler(
    {
      ...numberFormatOptions,
      thousandSeparator:
        restProps.thousandSeparator ?? numberFormatOptions.thousandSeparator,
    },
    onKeyDown,
  )

  const formattedPlaceholder = useMemo(
    () => numericFormatter(placeholder?.toString() ?? '', numberFormatOptions),
    [numberFormatOptions, placeholder],
  )

  const handleChange: OnValueChange = useCallback(
    (values) => {
      onChange?.(values.floatValue)
    },
    [onChange],
  )

  return (
    <NumericFormat
      value={value ?? ''}
      onValueChange={handleChange}
      name={name}
      customInput={RawTextInput}
      placeholder={formattedPlaceholder}
      onKeyDown={handleKeyDown}
      {...numberFormatOptions}
      {...restProps}
    />
  )
}
